<template>
  <v-container>
    <v-row>
      <v-col :cols="12" v-if="loading" class="d-flex">
        <v-progress-circular indeterminate class="mx-auto" />
      </v-col>
      <v-col :cols="12" v-else>
        <v-card max-width="800px" class="mx-auto" :elevation="8">
          <v-card-title class="text-h4">
            Your Team
          </v-card-title>
          <v-card-subtitle class="caption pl-5">
            {{$store.getters.getTeamOwnerEmailAddress ? $store.getters.getTeamOwnerEmailAddress : ''}}
          </v-card-subtitle>
          <v-card-text>
            <v-row>
              <v-col :cols="12" v-if="!$store.getters.isSubscribed">
                You aren't subscribed.  You must subscribe to create a team.
                <v-btn @click="$router.push({
                                path: '/account',
                                query: {
                                  subscribe: true
                                }
                                })"
                       color="primary"
                       class="text-none ml-2 mr-2">
                  Subscribe</v-btn>
              </v-col>
              <v-col :cols="12" v-else-if="$store.state.account.subscription.quantity === 1">
                Your subscription only allows for a single member (you).  To create a team, you must increase your subscription quantity on the Manage Subscription page.
                <v-btn @click="openCustomerPortal">
                  Open Manage Subscription Page
                </v-btn>
              </v-col>
              <v-col v-else-if="!$store.getters.isTeamOwner">
                {{`You are a member of ${$store.getters.getTeamOwnerEmailAddress}'s team.`}}
                <v-btn text @click="removeSelfFromTeam" :disabled="removingTeamMember" :loading="removingTeamMember" color="warning">
                  Leave team
                </v-btn>
              </v-col>
              <template v-else-if="$store.getters.isTeamOwner">
                <v-col :cols="12">
                  <div class="text-h5 black--text">Team Members</div>
                  <div>You can add and remove team members below.</div>
                </v-col>
                <v-col :cols="12" v-for="member of $store.state.account.subscription.members" :key="member.accountId">
                  <v-card outlined>
                    <v-card-text class="d-flex">
                      <div class="text-h6 d-inline-block">
                        <div class="black--text">{{member.email}}</div>
                        <div class="caption">
                          <span v-if="member.email.toLowerCase().trim() === $store.getters.getTeamOwnerEmailAddress">
                            You cannot remove yourself from the team
                          </span>
                        </div>
                      </div>
                      <div class="ml-auto my-auto">
                        <v-btn text x-small class="ml-2 " @click="removeFromTeam(member)"
                         :disabled="removingTeamMember || member.email.toLowerCase().trim() === $store.getters.getTeamOwnerEmailAddress"
                         :loading="member.loading" color="error">
                            <v-icon class="mr-1">mdi-delete</v-icon>
                            Remove
                        </v-btn>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </template>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-alert type="info" outlined v-if="$store.state.account.subscription && $store.state.account.subscription.members && $store.state.account.subscription.members.length >= $store.state.account.subscription.quantity">
              <span>
                {{`Your current subscription only allows for ${$store.state.account.subscription.quantity} team members.  You need to upgrade your subscription to add more team members.`}}
              </span>
            </v-alert>
            <v-btn
                v-else-if="$store.getters.getTeamOwnerEmailAddress === $store.state.account.email"
                color="primary"
                :disabled="addTeamMemberDialog"
                :loading="addTeamMemberDialog"
                @click="openAddTeamMemberDialog" class="pa-5">
              <v-icon class="mr-3">mdi-account-plus</v-icon>
              Add Team Member
            </v-btn>
          </v-card-actions>
        </v-card>
        <v-dialog v-model="addTeamMemberDialog" max-width="600px" persistent :fullscreen="$vuetify.breakpoint.xs">
          <v-card>
            <v-card-title>
              Add Team Member
            </v-card-title>
            <v-card-text>
              Please enter the team member's email address below:
              <v-text-field
                  outlined
                  label="Email"
                  color="primary"
                  v-model="dialogInfo.email"
                  hint="Please enter the team members work email address."
                  :persistent-hint="true"
                  :disabled="submittingNewTeamMember"
              />
              <v-text-field
                  outlined
                  :color="dialogInfo.email === dialogInfo.confirmedEmail && dialogInfo.email ? 'primary' : 'error'"
                  label="Confirm Email"
                  v-model="dialogInfo.confirmedEmail"
                  hint="Please reenter the email address to ensure correct spelling."
                  :persistent-hint="true"
                  :error="dialogInfo.confirmedEmail !== dialogInfo.email"
                  :disabled="submittingNewTeamMember"
              />
            </v-card-text>
            <v-card-actions>
              <v-btn @click="cancel" :disabled="submittingNewTeamMember">
                Cancel
              </v-btn>
              <v-spacer />
              <v-btn color="primary" @click="addTeamMember" :loading="submittingNewTeamMember" :disabled="submittingNewTeamMember || dialogInfo.confirmedEmail !== dialogInfo.email">
                Submit
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import Api from "@/services/aws/Api";
import StoreConstants from "@/store/StoreConstants";
import Utils from "@/services/Utils";

export default {
  name: "Team.vue",
  data() {
    return {
      loading: true,
      loadingCustomerPortal: false,
      addTeamMemberDialog: false,
      dialogInfo: {
        email: '',
        confirmedEmail: ''
      },
      submittingNewTeamMember: false,
      removingTeamMember: false
    }
  },
  methods: {
    cancel() {
      this.addTeamMemberDialog = false
      this.dialogInfo = {
        email: '',
        confirmedEmail: ''
      }
    },
    openAddTeamMemberDialog() {
      this.addTeamMemberDialog = true
    },
    async addTeamMember() {
      this.submittingNewTeamMember = true
      try {
        const currentMemberCount = this.$store.state.account.subscription.members.length

        const response = await Api.addToTeam(this.$store.state.credentials, {
          email: this.dialogInfo.email
        })

        if(response.data.errorMessage) {
          this.$swal(response.data.errorMessage)
        } else {
          for(let c = 0; c < 5; c++) {
            await this.$store.dispatch(StoreConstants.actions.loadAccount)
            if(this.$store.state.account.subscription.members.length !== currentMemberCount) {
              break
            } else {
              await Utils.sleep(1000)
            }
          }
          this.cancel()
        }

      } catch (e) {
        console.log(e)
        await this.$swal(e.toString())
        window.location.reload()
      }
      finally {
        this.submittingNewTeamMember = false
      }
    },
    async removeFromTeam(member) {
      this.removingTeamMember = true
      member.loading = true

      try {

        const answer = await this.$swal({
          title: `Remove from team`,
          text: `Are you sure you'd like to remove ${member.email} from your team?`,
          showDenyButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes',
          denyButtonText: 'Cancel'
        })

        console.log(answer)
        if(answer.isConfirmed) {
          const currentMemberCount = this.$store.state.account.subscription.members.length

          const response = await Api.removeFromTeam(this.$store.state.credentials, {
            email: member.email
          })

          for(let c = 0; c < 5; c++) {
            await this.$store.dispatch(StoreConstants.actions.loadAccount)
            if(this.$store.state.account.subscription.members.length !== currentMemberCount) {
              break
            } else {
              await Utils.sleep(1000)
            }
          }
        }
      } catch (e) {
        console.log(e)
        await this.$swal(e.toString())
        window.location.reload()
      }
      finally {
        member.loading = false
        this.removingTeamMember = false
      }
    },
    async removeSelfFromTeam() {
      this.removingTeamMember = true

      try {
        const answer = await this.$swal({
          title: `Remove from team`,
          text: `Are you sure you'd like to remove yourself from ${this.$store.getters.getTeamOwnerEmailAddress}'s team?`,
          showDenyButton: true,
          reverseButtons: true,
          confirmButtonText: 'Yes',
          denyButtonText: 'Cancel'
        })

        console.log(answer)
        if(answer.isConfirmed) {
          const currentSubscriptionId = this.$store.state.account.subscriptionId

          const response = await Api.removeSelfFromTeam(this.$store.state.credentials, {
            email: this.$store.getters.getTeamOwnerEmailAddress
          })

          for(let c = 0; c < 5; c++) {
            await this.$store.dispatch(StoreConstants.actions.loadAccount)
            if(this.$store.state.account.subscriptionId !== currentSubscriptionId) {
              break
            } else {
              await Utils.sleep(1000)
            }
          }
        }
      } catch (e) {
        console.log(e)
        await this.$swal(e.toString())
        window.location.reload()
      }
      finally {
        this.removingTeamMember = false
      }
    },
    async openCustomerPortal() {
      this.loadingCustomerPortal = true
      await this.$router.push({
        path: 'account',
        query: {
          openCustomerPortal: true
        }
      })
      this.loadingCustomerPortal = false
    }
  },
  mounted() {
    const init = async () => {
      await this.$store.state.loadAccountPromise
      this.loading = false
    }

    init()
  }
}
</script>

<style scoped>

</style>